import React, { useState } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import confetti from 'canvas-confetti';
import {
  useGetAllPersonas,
  useGetUser,
  useGetPersonaProfiles,
  useUpdatePersonaProfile,
  useCreatePersonaProfile,
  useDeletePersonaProfile,
} from '../../hooks/api-hooks';
import { ProfilesSection } from './profile-section';
import PreviewModal from './preview-modal';
import { GetAllPersona, UpdatePersonaProfileBody } from '../../api/types';
import { Link } from 'react-router-dom';

interface NeoFaceCardProps {
  persona: GetAllPersona;
  onSelect: (persona: GetAllPersona) => void;
}

const NeoFaceCard: React.FC<NeoFaceCardProps> = ({ persona, onSelect }) => {
  return (
    <div className='bg-white shadow rounded-lg overflow-hidden group'>
      {/* Image Container */}
      <div className='relative aspect-square overflow-hidden'>
        <img
          src={persona.swappedFaceUrl}
          alt='Neoface'
          className='h-full w-full object-cover transition-transform duration-300 group-hover:scale-105'
        />

        {/* Hover Overlay */}
        <div
          onClick={() => onSelect(persona)}
          className='absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-200 cursor-pointer'>
          <div className='absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200'>
            <MagnifyingGlassIcon className='h-8 w-8 text-white drop-shadow-lg' />
          </div>
        </div>
      </div>

      {/* Action Footer */}
      <div className='p-4'>
        <div className='flex justify-between items-center'>
          <button
            onClick={() => onSelect(persona)}
            className='flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-500 transition-colors duration-150'
          >
            Preview & Select
          </button>
        </div>
      </div>
    </div>
  );
};

export default function MyNeofaces() {
  const [selectedPersona, setSelectedPersona] = useState<GetAllPersona | null>(null);
  const [isSelecting, setIsSelecting] = useState(false);


  const { mutateAsync: createProfile } = useCreatePersonaProfile();
  const { mutateAsync: updateProfile } = useUpdatePersonaProfile();
  const { mutateAsync: deleteProfile } = useDeletePersonaProfile();

  const personasQuery = useGetAllPersonas();
  const user = useGetUser();
  const profiles = useGetPersonaProfiles();

  const handleSelectPersona = async (profileId: string, persona: GetAllPersona) => {
    setIsSelecting(true);
    try {
      await updateProfile({ profileId, updateProfileBody: { personaId: persona.id } });
      confetti({
        origin: { y: 0.3 },
        spread: 160,
        startVelocity: 30,
        particleCount: 200,
        angle: 90,
        zIndex: 9999,
      });
      await user.refetch();
      await profiles.refetch()
      setSelectedPersona(null);
    } finally {
      setIsSelecting(false);
    }
  };

  const handleAddNewProfile = async () => {
    await createProfile({})
    profiles.refetch();
  }

  const handleDeleteProfile = async (profileId: string) => {
    await deleteProfile({ profileId });
    profiles.refetch();
  }

  const handleEditProfile = async (profileId: string, body: UpdatePersonaProfileBody) => {
    await updateProfile({profileId, updateProfileBody: body});
    profiles.refetch();
  }


  return (
    <>
      {/* Header */}
      <div className='mb-6'>
        <h1 className='text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold'>
          My Neofaces
        </h1>
        <p className='text-slate-500'>View and manage your profiles</p>
      </div>

      {/* Current Neoface Section */}
      {!personasQuery.isLoading && user.data?.persona && profiles.data && (
        <ProfilesSection
          profiles={profiles.data}
          onAddProfile={handleAddNewProfile}
          onEditProfile={handleEditProfile}
          onDeleteProfile={handleDeleteProfile}
        />
      )}

      {/* All Neofaces Section */}
      <div className='mb-4 flex justify-between items-center'>
        <h2 className='text-lg font-medium text-slate-800'>Previous Neofaces</h2>
        <Link
          to="/create-neoface"
          className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600
         text-white text-sm font-medium rounded-lg hover:bg-indigo-500
         transition-colors duration-150"
        >
          Create New Face
        </Link>
      </div>

      {/* Loading State */}
      {personasQuery.isLoading && (
        <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4'>
          {Array.from({ length: 8 }).map((_, index) => (
            <div key={index} className='bg-white shadow rounded-lg overflow-hidden'>
              <div className='aspect-square bg-gray-200 animate-pulse' />
              <div className='p-4'>
                <div className='h-10 bg-gray-200 animate-pulse rounded-lg' />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Neofaces Grid */}
      {!personasQuery.isLoading && personasQuery.data && (
        <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4'>
          {personasQuery.data.map((persona) => (
            <NeoFaceCard
              key={persona.id}
              persona={persona}
              onSelect={(p) => setSelectedPersona(p)}
            />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!personasQuery.isLoading && (!personasQuery.data || personasQuery.data.length === 0) && (
        <div className='rounded-lg bg-white p-8 text-center'>
          <p className='text-gray-500'>You haven't created any neofaces yet.</p>
        </div>
      )}

      {/* Preview Modal */}
      {selectedPersona &&
        <PreviewModal
          isOpen={true}
          onClose={() => setSelectedPersona(null)}
          persona={selectedPersona}
          onSelect={handleSelectPersona}
          isSelecting={isSelecting}
          profiles={profiles.data ? profiles.data : []}
          defaultProfileId={''} />
      }
    </>
  );
}